import { useQuery } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HistoryNavigationControl } from 'components/minute/controls/HistoryNavigationControl';
import { MarkersListControl } from 'components/minute/controls/MarkersListControl';
import { HeaderCenter } from 'components/minute/layout/HeaderCenter';
import { MapLayout } from 'elements/layout/editor/MapLayout';
import { ControlLayout } from 'elements/layout/minute/ControlLayout';
import { MinuteHeaderLayout } from 'elements/layout/minute/MinuteHeaderLayout';
import { MinuteFloors } from 'elements/layout/minute/MinuteHeaderLayout/MinuteFloors';
import { MinutePromotions } from 'elements/layout/minute/MinuteHeaderLayout/MinutePromotions';
import { MinuteSearch } from 'elements/layout/minute/MinuteHeaderLayout/MinuteSearch';
import { MinuteWelcome } from 'elements/layout/minute/MinuteHeaderLayout/MinuteWelcome';
import { RightPanelLayout } from 'elements/layout/minute/RightPanelLayout';
import {
    WorldMapStories,
    WorldMapStoriesVariables
} from 'graphql/query/__generated__/WorldMapStories';
import { WORLD_MAP_STORIES } from 'graphql/query/worldQuery';
import { useWorldMapLocalStorage } from 'libs/hooks/ownership/useWorldMapLocalStorage';
import { EColors } from 'libs/styles/styling';
import { useContext, useEffect } from 'react';
import { CtxApp } from 'session/ctxApp';
import { CtxDetail } from 'session/ctxDetail';
import { CtxEdit } from 'session/ctxEdit';
import { CtxView } from 'session/ctxView';
import styled, { css } from 'styled-components/macro';

interface Props {
    className?: string;
    worldMapId: string;
    hideHeader?: boolean;
}

export const MinuteEditorLayout = (props: Props): JSX.Element => {
    const ctxEdit = useContext(CtxEdit);
    const ctxView = useContext(CtxView);
    const ctxDetail = useContext(CtxDetail);
    const ctxApp = useContext(CtxApp);
    const { getTokenForMapId } = useWorldMapLocalStorage();
    const { worldMapId } = props;

    const storiesResult = useQuery<WorldMapStories, WorldMapStoriesVariables>(WORLD_MAP_STORIES, {
        variables: { worldMapId, token: getTokenForMapId(worldMapId) },
        skip: !worldMapId
    });

    useEffect(() => {
        ctxApp.setMinuteEditor(true);
        return () => {
            ctxEdit?.reset();
            ctxView?.resetMap();
            ctxDetail?.reset();
        };
    }, []);

    useEffect(() => {
        if (ctxEdit.map) {
            console.log('Map', ctxEdit.map);
            ctxDetail.setMap(ctxEdit.map);
        }
    }, [ctxEdit.map]);

    useEffect(() => {
        if (storiesResult.data?.publishedStoriesForWorldMap) {
            console.log('Details');
            ctxView.setAllDetails(storiesResult.data.publishedStoriesForWorldMap);
        }
    }, [storiesResult.data]);

    return (
        <S_MinuteEditorLayout className={props.className}>
            {!props.hideHeader && (
                <MinuteHeaderLayout>
                    <HeaderCenter />
                </MinuteHeaderLayout>
            )}

            <S_BodyLayout $loaded={!!ctxEdit.map}>
                <MapLayout worldMapId={worldMapId}>
                    <ControlLayout />
                    <HistoryNavigationControl />
                    <MarkersListControl />
                    <S_Search />
                    <S_MinutePromotions />
                    <S_MinuteWelcome />
                    <S_MinuteFloors />
                </MapLayout>
                <RightPanelLayout />
            </S_BodyLayout>
            {!ctxEdit.map && (
                <S_LoadingBlock>
                    <S_Loading icon={['far', 'spinner']} spin />
                </S_LoadingBlock>
            )}
        </S_MinuteEditorLayout>
    );
};

const S_MinuteEditorLayout = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    max-height: 100vh;
    align-items: stretch;
    position: relative;
`;

const S_Search = styled(MinuteSearch)`
    position: absolute;
    top: 15px;
    right: 15px;
    z-index: 2;
`;

const S_MinuteFloors = styled(MinuteFloors)`
    position: absolute;
    left: 187px;
    top: 15px;
    z-index: 2;
`;

const S_MinutePromotions = styled(MinutePromotions)`
    position: absolute;
    top: 68px;
    right: 15px;
    z-index: 1;
`;

const S_MinuteWelcome = styled(MinuteWelcome)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 5;
    width: 450px;
`;

const S_BodyLayout = styled.div<{ $loaded: boolean }>`
    width: 100%;
    flex: 1;
    height: 1px; //hack
    display: flex;
    ${(props) => {
        if (props.$loaded) {
            return css`
                display: flex;
            `;
        } else {
            return css`
                display: none;
            `;
        }
    }}
`;

const S_LoadingBlock = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background-color: #efefef;
`;

const S_Loading = styled(FontAwesomeIcon)`
    color: ${EColors.DARK};
    font-size: 30px;
`;
